// =============================================================================
// ROLE-BASED VIEW CONFIGURATION — Single Source of Truth
// =============================================================================
// PURPOSE: Declares what every role sees across the entire app:
//   - sidebar: which navigation section groups are visible
//   - dashboard: which dashboard sections render (in order)
//   - pages: which route paths the role can access (used by RouteGuard)
//
// HOW TO USE:
//   import { roleConfig } from '@/config/roleConfig';
//   const view = roleConfig[user.role]; // → { sidebar, dashboard, pages }
//
// ADDING A NEW ROLE:
//   1. Add the role to UserRole in types/core.ts
//   2. Add an entry here with sidebar, dashboard, and pages
//   3. That's it — Sidebar, Dashboard, and RouteGuard all read from this config
//
// ARCHITECTURE DECISION (2026-02-03):
//   Client (client) currently shares the same app shell and sidebar as
//   internal roles, just with fewer links visible. This is the pragmatic choice
//   for now — it works and ships fast.
//
//   FUTURE: When Dynamis scales past ~20 clients or the client experience
//   diverges significantly, extract client into a completely separate
//   PortalShell with its own layout, navigation, and routing. The roleConfig
//   pattern makes this migration straightforward: just stop rendering the
//   shared shell for client and route them to the portal shell instead.
//   See brainstorm context in .claude/plans/eventual-wishing-squid.md
// =============================================================================

import type { UserRole, DashboardSection, SidebarSection, RoleView } from "../types/core";
import { canOpenPageAsTempDialer } from "./tempDialers";

// All protected route paths in the app
export const ALL_PAGES = [
  "/admin/dashboard",
  "/admin/transactions",
  "/admin/installments",
  "/admin/leaderboard",
  "/admin/calendar",
  "/admin/instagram",
  "/admin/dm-analytics",
  "/admin/dial-analytics",
  "/admin/lead-matches",
  "/admin/heatmap",
  "/admin/calendly",
  "/admin/precall-compliance",
  "/admin/invoice",
  "/admin/mastermind",
  "/admin/winners",
  "/admin/background",
  "/admin/registration-links",
  "/admin/portal",
  "/admin/rep-portal",
  "/admin/setter-timelines",
  "/admin/lead-search",
  "/admin/call-search",
  "/admin/call-reviews",
  "/admin/sidebar-viewer",
  "/admin/triage",
  "/admin/stoplights",
  "/admin/sop-library",
  "/admin/workforce",
  "/admin/course", // Course reader — exec, manager, closer, setter, dialer (no longer owner-only, 2026-10-06)
  "/admin/handbook", // Dynamis Handbook — exec, manager, closer, setter, dialer (HB-END A, 2026-10-08)
  "/admin/quiz", // Daily course quiz — reps take it, exec/manager preview (REP_ONBOARDING, 2026-10-08)
  "/admin/welcome", // Welcome video — standalone, no-skip (COURSE_PAGE plan welcome-video)
] as const;

export const roleConfig: Record<UserRole, RoleView> = {
  // ─────────────────────────────────────────────────────────────────────────
  // EXECUTIVE — sees everything
  // ─────────────────────────────────────────────────────────────────────────
  exec: {
    sidebar: ["management", "execTools"],
    dashboard: [
      "hero",
      "funnelHealth",
      "leaderboard", // Bento: inline podium (olympic visual, role-separated)
      "transactions", // Bento: 8-row snippet
      "installments", // Bento: 8-row snippet
      "upcomingCalls", // Bento: org-wide with pre-call dots
      "sufferingLeads", // Bento: org-wide suffering leads
    ],
    pages: [...ALL_PAGES], // exec sees everything
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CLIENT — revenue stats + portal (tasks/files/comms)
  // See ARCHITECTURE DECISION above for future PortalShell migration plan
  // ─────────────────────────────────────────────────────────────────────────
  client: {
    sidebar: ["portal", "management"],
    dashboard: ["clientStats"],
    pages: [
      "/admin/portal",
      "/admin/transactions",
      "/admin/installments",
      "/admin/calendar",
      "/admin/sop-library", // SOP Library — visible to all roles (Mako, Jul 2026); content gated server-side
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MANAGER — pod performance, team oversight, analytics
  // ─────────────────────────────────────────────────────────────────────────
  manager: {
    sidebar: ["management"],
    dashboard: ["hero", "kpiGrid", "installments", "leaderboard", "team", "transactions"],
    pages: [
      "/admin/dashboard",
      "/admin/transactions",
      "/admin/installments",
      "/admin/leaderboard",
      "/admin/calendar",
      "/admin/rep-portal",
      "/admin/instagram",
      "/admin/setter-timelines",
      "/admin/lead-search",
      "/admin/call-search",
      "/admin/sop-library", // SOP Library — visible to all roles (Mako, Jul 2026); content gated server-side
      "/admin/workforce", // Workforce people console — exec/manager only
      "/admin/course", // Course reader — reps + managers can open it (COURSE_PAGE deep links, AFK pick 2026-10-06)
      "/admin/handbook", // Dynamis Handbook — every role reads it first (HB-END A, 2026-10-08)
      "/admin/quiz", // Daily course quiz (REP_ONBOARDING, 2026-10-08)
      "/admin/welcome", // Welcome video — standalone, no-skip (COURSE_PAGE plan welcome-video)
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CLOSER — cash/revenue focus, own transactions, leaderboard
  // ─────────────────────────────────────────────────────────────────────────
  closer: {
    sidebar: ["management"],
    dashboard: ["closerStats", "financialGraph"],
    pages: [
      "/admin/rep-portal", // default landing page for reps
      "/admin/calendar",
      "/admin/transactions",
      "/admin/installments",
      "/admin/leaderboard",
      "/admin/instagram",
      "/admin/setter-timelines",
      "/admin/lead-search",
      "/admin/call-search",
      "/admin/call-reviews", // AI call reviews — all reps (Mako, Jul 2026)
      "/admin/triage", // Lost Leads — all reps (Mako, Jul 2026)
      "/admin/dial-analytics", // closers see dial scoreboard too (Mako, Jul 2026)
      "/admin/sop-library", // SOP Library — visible to all roles (Mako, Jul 2026); content gated server-side
      "/admin/course", // Course reader — reps + managers can open it (COURSE_PAGE deep links, AFK pick 2026-10-06)
      "/admin/handbook", // Dynamis Handbook — every role reads it first (HB-END A, 2026-10-08)
      "/admin/quiz", // Daily course quiz (REP_ONBOARDING, 2026-10-08)
      "/admin/welcome", // Welcome video — standalone, no-skip (COURSE_PAGE plan welcome-video)
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SETTER — quality metrics, DMs, calendar, leaderboard
  // ─────────────────────────────────────────────────────────────────────────
  setter: {
    sidebar: ["management"],
    dashboard: ["setterStats", "financialGraph"],
    pages: [
      "/admin/rep-portal", // default landing page for reps
      "/admin/calendar",
      "/admin/transactions",
      "/admin/installments",
      "/admin/leaderboard",
      "/admin/instagram",
      "/admin/dm-analytics",
      "/admin/setter-timelines",
      "/admin/lead-search",
      "/admin/call-search",
      "/admin/call-reviews", // AI call reviews — all reps (Mako, Jul 2026)
      "/admin/triage", // Lost Leads — all reps (Mako, Jul 2026)
      "/admin/sop-library", // SOP Library — visible to all roles (Mako, Jul 2026); content gated server-side
      "/admin/course", // Course reader — reps + managers can open it (COURSE_PAGE deep links, AFK pick 2026-10-06)
      "/admin/handbook", // Dynamis Handbook — every role reads it first (HB-END A, 2026-10-08)
      "/admin/quiz", // Daily course quiz (REP_ONBOARDING, 2026-10-08)
      "/admin/welcome", // Welcome video — standalone, no-skip (COURSE_PAGE plan welcome-video)
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // DIALER — call volume focus, minimal view + calendar for booking
  // ─────────────────────────────────────────────────────────────────────────
  dialer: {
    sidebar: ["management"],
    dashboard: ["dialerStats", "leaderboard"],
    pages: [
      "/admin/rep-portal", // landing page for dialers (pages[0]) — Dial HQ removed from sidebar Jul 2026
      "/admin/calendar",
      "/admin/transactions",
      "/admin/installments",
      "/admin/leaderboard",
      "/admin/instagram",
      "/admin/setter-timelines",
      "/admin/lead-search",
      "/admin/call-search",
      "/admin/call-reviews", // AI call reviews — all reps (Mako, Jul 2026)
      "/admin/triage", // Lost Leads — all reps (Mako, Jul 2026)
      "/admin/dial-analytics", // dialers see the org-wide dial scoreboard (transparency mirror of DM Analytics)
      "/admin/sop-library", // SOP Library — visible to all roles (Mako, Jul 2026); content gated server-side
      "/admin/course", // Course reader — reps + managers can open it (COURSE_PAGE deep links, AFK pick 2026-10-06)
      "/admin/handbook", // Dynamis Handbook — every role reads it first (HB-END A, 2026-10-08)
      "/admin/quiz", // Daily course quiz (REP_ONBOARDING, 2026-10-08)
      "/admin/welcome", // Welcome video — standalone, no-skip (COURSE_PAGE plan welcome-video)
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // VIEWER — catch-all for unknown roles. Empty = no access.
  // normalizeRole() maps unrecognized strings here. They see nothing.
  // ─────────────────────────────────────────────────────────────────────────
  viewer: {
    sidebar: [],
    dashboard: [],
    pages: [],
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Helper: check if a role has access to a specific page
// ─────────────────────────────────────────────────────────────────────────
export function canAccessPage(role: UserRole, path: string): boolean {
  // Exact match, plus sub-paths of an allowed page (param routes like
  // /admin/call-reviews/:reviewId inherit access from their parent page).
  return roleConfig[role].pages.some((p) => path === p || path.startsWith(`${p}/`));
}

// Per-person version: the role's pages plus a temporary dialer grant's pages
// (config/tempDialers.ts). Sidebar, RouteGuard and mobile nav call this one.
export function canAccessPageForUser(
  role: UserRole,
  path: string,
  userId: string | undefined | null
): boolean {
  return canAccessPage(role, path) || canOpenPageAsTempDialer(role, path, userId);
}

// ─────────────────────────────────────────────────────────────────────────
// Helper: check if a role's dashboard includes a specific section
// ─────────────────────────────────────────────────────────────────────────
export function hasDashboardSection(role: UserRole, section: DashboardSection): boolean {
  return roleConfig[role].dashboard.includes(section);
}

// ─────────────────────────────────────────────────────────────────────────
// Helper: check if a role's sidebar includes a specific section group
// ─────────────────────────────────────────────────────────────────────────
export function hasSidebarSection(role: UserRole, section: SidebarSection): boolean {
  return roleConfig[role].sidebar.includes(section);
}
